import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import qualityCapacityReportService from '../services/qualityCapacityReportService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Calendar,
  ChevronRight,
  FileSpreadsheet,
  Loader2,
  Play,
  RefreshCw,
  Search,
} from 'lucide-react';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `${(num * 100).toFixed(2)}%`;
};

const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 1);
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
};

const QualityCapacityReportRunsPage = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 1,
  });
  const pageSize = 20;

  const loadRuns = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await qualityCapacityReportService.getRuns(page, pageSize);
      setRuns(data.runs || []);
      setPagination(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || '加载报表记录失败');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setError('');
      const run = await qualityCapacityReportService.createRun(
        dateRange.startDate,
        dateRange.endDate
      );
      navigate(`/reports/quality-capacity/runs/${run._id || run.run_id}`);
    } catch (err) {
      setError(err.response?.data?.error || err.message || '生成报表失败');
    } finally {
      setGenerating(false);
    }
  };

  const handleViewRun = (runId) => {
    navigate(`/reports/quality-capacity/runs/${runId}`);
  };

  return (
    <Layout title="质量与产能报表" activeTab="reports">
      <div className="max-w-[1600px] mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">质量与产能报表</h1>
            <p className="text-sm text-muted-foreground mt-1">
              生成并管理质量与产能报表运行记录
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadRuns} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-2">刷新</span>
            </Button>
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Search className="h-4 w-4" />
              生成新报表
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">开始日期</Label>
                <div className="relative">
                  <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="startDate"
                    name="startDate"
                    type="date"
                    value={dateRange.startDate}
                    onChange={handleDateChange}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">结束日期</Label>
                <div className="relative">
                  <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="endDate"
                    name="endDate"
                    type="date"
                    value={dateRange.endDate}
                    onChange={handleDateChange}
                    className="pl-9"
                  />
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={handleGenerate} disabled={generating}>
                {generating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                <span className="ml-2">生成报表</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="mb-4 p-4 rounded-md bg-destructive/10 text-destructive text-sm">
            {error}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              历史报表记录
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="whitespace-nowrap">运行编号</TableHead>
                    <TableHead className="whitespace-nowrap">报表名称</TableHead>
                    <TableHead className="whitespace-nowrap">日期范围</TableHead>
                    <TableHead className="text-right whitespace-nowrap">行数</TableHead>
                    <TableHead className="text-right whitespace-nowrap">交检总数</TableHead>
                    <TableHead className="text-right whitespace-nowrap">良品数</TableHead>
                    <TableHead className="text-right whitespace-nowrap">报废数</TableHead>
                    <TableHead className="text-right whitespace-nowrap">良率</TableHead>
                    <TableHead className="text-right whitespace-nowrap">总产值</TableHead>
                    <TableHead className="text-center whitespace-nowrap">是否达标</TableHead>
                    <TableHead className="whitespace-nowrap">生成人</TableHead>
                    <TableHead className="whitespace-nowrap">生成时间</TableHead>
                    <TableHead className="text-right whitespace-nowrap">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && runs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={13} className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground mt-2">加载中...</p>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && runs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={13} className="text-center py-8 text-muted-foreground">
                        暂无报表记录，点击上方「生成报表」创建第一份报表
                      </TableCell>
                    </TableRow>
                  )}
                  {runs.map((run) => (
                    <TableRow
                      key={run._id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleViewRun(run._id || run.run_id)}
                    >
                      <TableCell className="whitespace-nowrap font-medium">{run.run_id}</TableCell>
                      <TableCell className="whitespace-nowrap">{run.report_name}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {run.filters?.start_date} ~ {run.filters?.end_date}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">{run.row_count}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {formatNumber(run.summary?.total_inspected)}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {formatNumber(run.summary?.total_good)}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {formatNumber(run.summary?.total_scrapped)}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {formatPercent(run.summary?.yield_rate)}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {formatNumber(run.summary?.total_output_value)}
                      </TableCell>
                      <TableCell className="text-center whitespace-nowrap">
                        {run.summary?.is_target_met ? (
                          <Badge variant="outline" className="text-green-600 border-green-600">是</Badge>
                        ) : (
                          <Badge variant="outline" className="text-red-600 border-red-600">否</Badge>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {run.created_by_name || '-'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {formatDateTime(run.created_at)}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewRun(run._id || run.run_id);
                          }}
                        >
                          查看 <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  第 {pagination.page} / {pagination.total_pages} 页，共 {pagination.total} 条
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || loading}
                  >
                    上一页
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                    disabled={page >= pagination.total_pages || loading}
                  >
                    下一页
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default QualityCapacityReportRunsPage;
